import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { TabsLayout, PageNavigation, PageBackground } from '../shared';
import { PersonOverview, getPersonInfoColumns, PersonAbout, PersonSettings } from './components';
import { usePersonDetailData } from './hooks';

export function PersonDetail() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  
  // Get person detail data from API
  const { personDetail, loading, error } = usePersonDetailData();

  // Show loading state with page structure
  if (loading) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          {/* Navigation skeleton */}
          <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse"></div>
          
          {/* Profile header skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="flex-1 space-y-3">
                <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
              </div>
            </div>
          </div>
          
          {/* Tabs skeleton */}
          <div className="flex gap-4 border-b border-gray-200 mb-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
            ))}
          </div>
          
          {/* Content skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </PageBackground>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageBackground>
        <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center max-w-md">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <LucideIcons.AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Profile</h3>
                <p className="text-red-700 mb-4">{error.message}</p>
                <button
                  onClick={() => navigate('/community')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Back to People
                </button>
              </div>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }

  // Show not found state
  if (!personDetail) {
    return (
      <div className="w-full bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LucideIcons.UserX className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Person Not Found</h3>
          <p className="text-gray-600 mb-4">The person you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/community')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to People
          </button>
        </div>
      </div>
    );
  }

  const fullName = `${personDetail.firstName} ${personDetail.lastName}`;

  // Define kebab menu items (organized by category)
  const kebabMenuItems = [
    // Communication
    { label: 'Send Email', onClick: () => console.log('Send Email'), icon: LucideIcons.Mail },
    { label: 'Send SMS', onClick: () => console.log('Send SMS'), icon: LucideIcons.MessageSquare },
    { label: 'Check-In Message', onClick: () => console.log('Check-In Message'), icon: LucideIcons.MessageCircle },
    
    // Family & Relationships
    { label: 'Add Family Member', onClick: () => console.log('Add Family Member'), icon: LucideIcons.UserPlus },
    { label: 'Merge Duplicates', onClick: () => console.log('Merge Duplicates'), icon: LucideIcons.Combine },
    
    // Files & Reports
    { label: 'Add A File', onClick: () => console.log('Add A File'), icon: LucideIcons.FileUp },
    { label: 'Profile Report', onClick: () => console.log('Profile Report'), icon: LucideIcons.FileText },
    { label: 'Download Contact Card', onClick: () => console.log('Download Contact Card'), icon: LucideIcons.Download },
    { label: 'View Change Log', onClick: () => console.log('View Change Log'), icon: LucideIcons.History },
    
    // Settings & Permissions
    { label: 'Admin Permissions', onClick: () => console.log('Admin Permissions'), icon: LucideIcons.Key },
    { label: 'Update Login Details', onClick: () => console.log('Update Login Details'), icon: LucideIcons.Lock },
    { label: 'My Fit', onClick: () => console.log('My Fit'), icon: LucideIcons.Target },
  ];

  return (
    <PageBackground>
      <main className="max-w-[1600px] mx-auto p-4 sm:p-8">
        {/* Page Navigation with Kebab Menu */}
        <div className="mb-6">
          <PageNavigation
            showBreadcrumbs={true}
            breadcrumbs={[
              { label: 'All People', onClick: () => navigate('/community') },
              { label: fullName }
            ]}
            kebabMenuItems={kebabMenuItems}
          />
        </div>
        
        {/* Person Overview with Info Section */}
        <PersonOverview 
          personDetail={personDetail}
          infoColumns={getPersonInfoColumns(personDetail)}
        />
        
        {/* Tabs Section */}
        <TabsLayout
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={[
            {
              id: 'about',
              label: 'About',
              content: <PersonAbout personDetail={personDetail} />,
            },
            {
              id: 'involvement',
              label: 'Involvement',
              content: (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  Involvement content coming soon
                </div>
              ),
            },
            {
              id: 'notes',
              label: 'Notes',
              content: (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  Notes content coming soon
                </div>
              ),
            },
            {
              id: 'messages',
              label: 'Messages',
              content: (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  Messages content coming soon
                </div>
              ),
            },
            {
              id: 'settings',
              label: 'Settings',
              content: <PersonSettings personDetail={personDetail} />,
            },
          ]}
        />
      </main>
    </PageBackground>
  );
}
